import { useState, useCallback, useEffect } from "react"
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../components/ui/collapsible"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

export function CollapsibleRenderer({ element }: RendererProps) {
  const { dispatchEvent, resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: collapsible.variant === "open" is a legacy initial-state marker
  // used by defaultOpen below; data-variant preserves the raw token.
  const atomDefaults = useAtomDefaults("collapsible")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // Standard bind: element.bind → state boolean
  const boundFromBind = element.bind
    ? resolveSymbol(element.bind.startsWith("#{") ? element.bind : `#{${element.bind}}`)
    : undefined

  // Legacy: options.open binding
  const openBinding = (element.options as Record<string, unknown> | undefined)?.open
  const resolvedOpen =
    openBinding != null ? resolveSymbol(String(openBinding)) : undefined

  // Effective external value: prefer element.bind, then options.open
  const authoredOpen = element.open !== undefined
    ? typeof element.open === "string"
      ? resolveSymbol(element.open)
      : element.open
    : undefined
  const externalOpen = boundFromBind ?? authoredOpen ?? resolvedOpen

  const defaultOpen =
    (element as unknown as Record<string, unknown>).defaultOpen === true ||
    element.variant === "open"

  const [isOpen, setIsOpen] = useState(() => {
    if (externalOpen !== undefined) return Boolean(externalOpen)
    return defaultOpen
  })

  useEffect(() => {
    if (externalOpen !== undefined) {
      const ext = Boolean(externalOpen)
      if (ext !== isOpen) setIsOpen(ext)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalOpen])

  // Type-based: find trigger/content by type, fallback to position [0] for legacy
  const children = element.children ?? []
  const trigger =
    children.find(c => c.type === "collapsible-trigger") ?? children[0]
  const contentWrapper = children.find(c => c.type === "collapsible-content")
  const content = contentWrapper?.children ?? children.filter(c => c !== trigger)

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      dispatchEvent(element.id, "tap", { open })
    },
    [element.id, dispatchEvent],
  )

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={cn(stripPresetConflicts(element.className, undefined, element.designAuthorityClassName))}
      data-testid={`collapsible-${element.id}`}
      data-atom-type="collapsible"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    >
      {trigger && (
        trigger.type === "collapsible-trigger"
          ? <ElementRenderer element={trigger} />
          : (
            <CollapsibleTrigger className="cursor-pointer">
              <ElementRenderer element={trigger} />
            </CollapsibleTrigger>
          )
      )}
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        {content.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  )
}
